import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MESSAGE_CATEGORY_LABELS, type InboxItem, type MessageCategory } from '@gwc/shared';
import { inboxApi } from '../api/comms';
import { PageSkeleton } from '../components/Skeleton';

const dateLabel = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

function Item({ item, onRead }: { item: InboxItem; onRead: (id: string) => void }) {
  const category = MESSAGE_CATEGORY_LABELS[item.category as MessageCategory] ?? item.category;
  return (
    <article
      className={`card p-5 transition ${item.readAt ? '' : 'border-gold-500/40'}`}
      onMouseEnter={() => {
        if (!item.readAt) onRead(item.id);
      }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {!item.readAt ? (
              <span className="h-2 w-2 shrink-0 rounded-full bg-gold-500" aria-label="Unread" />
            ) : null}
            <h2 className="truncate font-display text-lg text-cream-50">{item.title}</h2>
          </div>
          <p className="mt-0.5 text-xs uppercase tracking-wide text-cream-300/45">
            {category}
            {item.senderName ? ` · ${item.senderName}` : ' · The club'}
          </p>
        </div>
        <time className="shrink-0 text-xs text-cream-300/50">{dateLabel(item.createdAt)}</time>
      </div>

      <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-cream-200">{item.body}</p>

      {item.link ? (
        <Link to={item.link} className="btn-ghost mt-4 inline-block text-sm">
          Open →
        </Link>
      ) : null}
    </article>
  );
}

export function Inbox() {
  const qc = useQueryClient();
  const [unreadOnly, setUnreadOnly] = useState(false);

  const query = useInfiniteQuery({
    queryKey: ['inbox', 'page', { unreadOnly }],
    queryFn: ({ pageParam }) =>
      inboxApi.list({ limit: 20, cursor: pageParam as string | undefined, unreadOnly }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => inboxApi.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inbox'] }),
  });
  const markAllRead = useMutation({
    mutationFn: () => inboxApi.markAllRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inbox'] }),
  });

  if (query.isLoading) return <PageSkeleton />;

  const items = query.data?.pages.flatMap((p) => p.items) ?? [];
  const unread = query.data?.pages[0]?.unreadCount ?? 0;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-cream-50">Inbox</h1>
          <p className="mt-1 text-sm text-cream-300/70">
            {unread > 0 ? `${unread} unread` : 'Everything from the club, in one place.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-cream-300/70">
            <input
              type="checkbox"
              className="h-4 w-4 accent-gold-500"
              checked={unreadOnly}
              onChange={(e) => setUnreadOnly(e.target.checked)}
            />
            Unread only
          </label>
          {unread > 0 ? (
            <button
              type="button"
              className="btn-ghost text-sm"
              disabled={markAllRead.isPending}
              onClick={() => markAllRead.mutate()}
            >
              Mark all read
            </button>
          ) : null}
        </div>
      </header>

      {!items.length ? (
        <div className="card p-12 text-center">
          <p className="text-sm text-cream-300/60">
            {unreadOnly ? 'Nothing unread.' : 'No messages yet. Club news will land here.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <Item key={item.id} item={item} onRead={(id) => markRead.mutate(id)} />
          ))}
        </div>
      )}

      {query.hasNextPage ? (
        <div className="text-center">
          <button
            type="button"
            className="btn-ghost"
            disabled={query.isFetchingNextPage}
            onClick={() => query.fetchNextPage()}
          >
            {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
